import {
    AiMessageTypes,
    Author,
    DEFAULT_THREAD_NAME,
    type AgentJsonContent,
    type AgentMessage,
    type AIMessageContext,
    type UserThreadMapping,
} from '@diagon-alley/common';
import {
    getAiChatMessages,
    getAiChatThread,
    useAiAnalystThread,
    useDeleteUserThread,
    useUpdateAiAnalystThread,
} from '@hooks/ai/useAiAnalyst';
import useAIChatRenderContext from '@hooks/ai/useAIChatRenderContext';
import useAIStreamMutation, {
    type StreamMessage,
} from '@hooks/ai/useAIStreamMutation';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import AiAnalystProviderContext from './context';
import {
    ActionType,
    type AiAnalystContext,
    type AiAnalystReducerState,
    type ExtractedKnowledgeItem,
    type FeedbackContent,
    type FeedbackItem,
    type MemoryContent,
    type UpdateThreadPayload,
} from './types';
import useAiChatThreadPersistence from './useAiChatThreadPersistence';

type Action =
    | { type: ActionType.SET_SHOW_AI_ANALYST; payload: boolean }
    | { type: ActionType.SET_AI_CHAT_MESSAGES; payload: Array<AgentMessage> }
    | {
          type: ActionType.SET_ACTIVE_THREAD;
          payload: {
              thread: UserThreadMapping;
              messages?: Array<AgentMessage>;
          };
      }
    | { type: ActionType.SET_AI_CONTEXT; payload: AIMessageContext | null }
    | { type: ActionType.ADD_STREAMED_MESSAGE_ID; payload: string }
    | { type: ActionType.SET_USER_FEEDBACK; payload: Record<string, boolean> }
    | {
          type: ActionType.SET_EXTRACTED_KNOWLEDGE;
          payload: ExtractedKnowledgeItem[];
      }
    | {
          type: ActionType.ADD_TO_KNOWLEDGE_QUEUE;
          payload: ExtractedKnowledgeItem[];
      }
    | { type: ActionType.REMOVE_FROM_KNOWLEDGE_QUEUE; payload: string }
    | {
          type: ActionType.ADD_TO_FEEDBACK_QUEUE;
          payload: FeedbackItem[];
      }
    | { type: ActionType.REMOVE_FROM_FEEDBACK_QUEUE; payload: string }
    | { type: ActionType.PUSH_TEXT_TO_INPUT; payload: string }
    | { type: ActionType.CLEAR_PUSHED_INPUT_TEXT }
    | {
          type: ActionType.UPDATE_KNOWLEDGE_CONTENT;
          payload: { id: string; content: string };
      }
    | {
          type: ActionType.OPEN_FEEDBACK_MODAL;
          payload: { messageId: string; threadId: string; traceId: string };
      }
    | { type: ActionType.CLOSE_FEEDBACK_MODAL }
    | { type: ActionType.UPDATE_ACTIVE_THREAD; payload: UserThreadMapping }
    | { type: ActionType.START_NEW_THREAD };

function reducer(
    state: AiAnalystReducerState,
    action: Action,
): AiAnalystReducerState {
    switch (action.type) {
        case ActionType.SET_SHOW_AI_ANALYST: {
            return {
                ...state,
                showAISideBar: action.payload,
            };
        }

        case ActionType.SET_USER_FEEDBACK: {
            return {
                ...state,
                userFeedback: {
                    ...state.userFeedback,
                    ...action.payload,
                },
            };
        }

        case ActionType.SET_AI_CHAT_MESSAGES: {
            const mergedMessages = [...state.aiChatMessages];

            action.payload.forEach((message) => {
                const existingIndex = mergedMessages.findIndex(
                    (existing) => existing.messageId === message.messageId,
                );

                if (existingIndex >= 0) {
                    mergedMessages[existingIndex] = message;
                } else {
                    mergedMessages.push(message);
                }
            });

            return {
                ...state,
                aiChatMessages: mergedMessages,
            };
        }

        case ActionType.SET_ACTIVE_THREAD: {
            return {
                ...state,
                activeThread: action.payload.thread,
                ...(action.payload.messages && {
                    aiChatMessages: action.payload.messages,
                }),
                streamedMessageIds: [],
                userFeedback: {},
                extractedKnowledge: [],
                feedbackQueue: [],
            };
        }

        case ActionType.UPDATE_ACTIVE_THREAD: {
            return {
                ...state,
                activeThread: { ...state.activeThread, ...action.payload },
            };
        }

        case ActionType.SET_AI_CONTEXT: {
            return {
                ...state,
                aiContext: action.payload,
            };
        }

        case ActionType.ADD_STREAMED_MESSAGE_ID: {
            return {
                ...state,
                streamedMessageIds: [
                    ...state.streamedMessageIds,
                    action.payload,
                ],
            };
        }

        case ActionType.SET_EXTRACTED_KNOWLEDGE: {
            return {
                ...state,
                extractedKnowledge: action.payload,
            };
        }

        case ActionType.ADD_TO_KNOWLEDGE_QUEUE: {
            return {
                ...state,
                extractedKnowledge: [
                    ...state.extractedKnowledge,
                    ...action.payload,
                ],
            };
        }

        case ActionType.REMOVE_FROM_KNOWLEDGE_QUEUE: {
            return {
                ...state,
                extractedKnowledge: state.extractedKnowledge.filter(
                    (item) => item.id !== action.payload,
                ),
            };
        }

        case ActionType.ADD_TO_FEEDBACK_QUEUE: {
            return {
                ...state,
                feedbackQueue: [...state.feedbackQueue, ...action.payload],
            };
        }

        case ActionType.REMOVE_FROM_FEEDBACK_QUEUE: {
            return {
                ...state,
                feedbackQueue: state.feedbackQueue.filter(
                    (item) => item.id !== action.payload,
                ),
            };
        }

        case ActionType.PUSH_TEXT_TO_INPUT: {
            return {
                ...state,
                pushedInputText: action.payload,
            };
        }

        case ActionType.CLEAR_PUSHED_INPUT_TEXT: {
            return {
                ...state,
                pushedInputText: null,
            };
        }

        case ActionType.UPDATE_KNOWLEDGE_CONTENT: {
            return {
                ...state,
                extractedKnowledge: state.extractedKnowledge.map((item) =>
                    item.id === action.payload.id
                        ? { ...item, content: action.payload.content }
                        : item,
                ),
            };
        }

        case ActionType.OPEN_FEEDBACK_MODAL: {
            return {
                ...state,
                feedbackModalOpen: true,
            };
        }

        case ActionType.CLOSE_FEEDBACK_MODAL: {
            return {
                ...state,
                feedbackModalOpen: false,
            };
        }

        case ActionType.START_NEW_THREAD: {
            return {
                ...state,
                activeThread: null,
                aiChatMessages: [],
                streamedMessageIds: [],
                userFeedback: {},
                extractedKnowledge: [],
                feedbackQueue: [],
            };
        }

        default: {
            throw new Error('Unexpected action');
        }
    }
}

const AiAnalystProvider: React.FC<React.PropsWithChildren<{}>> = ({
    children,
}) => {
    const [reducerState, dispatch] = useReducer(reducer, {
        showAISideBar: JSON.parse(
            localStorage.getItem('showAISideBar') ?? 'false',
        ),
        activeThread: null,
        aiChatMessages: [],
        aiContext: null,
        streamedMessageIds: [],
        userFeedback: {},
        extractedKnowledge: [],
        feedbackQueue: [],
        pushedInputText: null,
        isStreaming: false,
        feedbackModalOpen: false,
        isLoadingActiveThreadMessages: false,
        isDeletingThread: false,
    });

    const navigate = useNavigate();
    const { mutateAsync: mutateAsyncCreateAiAnalystThread } =
        useAiAnalystThread();
    const {
        mutateAsync: mutateAsyncDeleteThread,
        isLoading: isDeletingThread,
    } = useDeleteUserThread();
    const { mutateAsync: mutateAsyncUpdateThread } = useUpdateAiAnalystThread();

    const currentFeedbackModalDataRef = useRef<{
        messageId: string;
        threadId: string;
        traceId: string;
    } | null>(null);
    const [isLoadingActiveThreadMessages, setIsFetchingThreadMessages] =
        useState(false);
    const [statusText, setStatusText] = useState<string | undefined>(undefined);

    const responseCallback = useRef<((data: AgentJsonContent) => void) | null>(
        null,
    );
    const hasInitializedRef = useRef<boolean>(false);
    const aiChatMessagesRef = useRef<AgentMessage[]>([]);

    useEffect(() => {
        aiChatMessagesRef.current = reducerState.aiChatMessages;
    }, [reducerState.aiChatMessages]);

    const { getCurrentContext, getContextLocation, getStateForNavigation } =
        useAIChatRenderContext();

    const registerCallback = useCallback(
        (callback: (data: AgentJsonContent) => void) => {
            responseCallback.current = callback;
        },
        [],
    );

    const addStreamedMessageId = useCallback((messageId: string) => {
        dispatch({
            type: ActionType.ADD_STREAMED_MESSAGE_ID,
            payload: messageId,
        });
    }, []);

    const unregisterCallback = useCallback(() => {
        responseCallback.current = null;
    }, []);

    const setAiContext = useCallback((context: AIMessageContext | null) => {
        dispatch({
            type: ActionType.SET_AI_CONTEXT,
            payload: context,
        });
    }, []);

    const setUserFeedback = useCallback(
        (userFeedback: Record<string, boolean>) => {
            dispatch({
                type: ActionType.SET_USER_FEEDBACK,
                payload: userFeedback,
            });
        },
        [],
    );

    const setShowAISidebar = useCallback((showAISideBar: boolean) => {
        localStorage.setItem('showAISideBar', JSON.stringify(showAISideBar));
        dispatch({
            type: ActionType.SET_SHOW_AI_ANALYST,
            payload: showAISideBar,
        });
    }, []);

    const setAIChatMessages = useCallback((messages: Array<AgentMessage>) => {
        dispatch({
            type: ActionType.SET_AI_CHAT_MESSAGES,
            payload: messages,
        });
    }, []);

    const onSuccess = useCallback((json: AgentJsonContent) => {
        if (responseCallback.current) {
            responseCallback.current(json);
        }
    }, []);

    const { restoreThread, handleThreadCreated, clearCurrentAiThreadCookie } =
        useAiChatThreadPersistence({
            projectUuid: undefined, // Can be added later if needed
            onSetActiveThread: (payload) =>
                dispatch({
                    type: ActionType.SET_ACTIVE_THREAD,
                    payload,
                }),
            fetchThreadAndMessages: async (threadId: string) => {
                setIsFetchingThreadMessages(true);
                try {
                    const [messages, thread] = await Promise.all([
                        getAiChatMessages(threadId),
                        getAiChatThread(threadId),
                    ]);
                    return { thread, messages };
                } finally {
                    setIsFetchingThreadMessages(false);
                }
            },
            createThread: async () => {
                const created = await mutateAsyncCreateAiAnalystThread();
                return created;
            },
        });

    const handleCreateThread = useCallback(async () => {
        const resp = await mutateAsyncCreateAiAnalystThread();
        handleThreadCreated(resp);
        return resp;
    }, [mutateAsyncCreateAiAnalystThread, handleThreadCreated]);

    const addToKnowledgeQueue = useCallback(
        (memoryContent: MemoryContent, messageId: string, thread: string) => {
            const newItems = memoryContent.value.map((content: string) => ({
                id: uuidv4(),
                content,
                messageId,
                threadId: thread,
            }));
            dispatch({
                type: ActionType.ADD_TO_KNOWLEDGE_QUEUE,
                payload: newItems,
            });
        },
        [],
    );

    const removeFromKnowledgeQueue = useCallback((id: string) => {
        dispatch({
            type: ActionType.REMOVE_FROM_KNOWLEDGE_QUEUE,
            payload: id,
        });
    }, []);

    const acceptKnowledge = useCallback(
        async (item: ExtractedKnowledgeItem) => {
            try {
                removeFromKnowledgeQueue(item.id);
                // TODO: Implement knowledge base acceptance when backend is ready
                // await mutateAsyncAcceptMemory({...});
            } catch (error) {
                console.error('Failed to accept memory:', error);
            }
        },
        [removeFromKnowledgeQueue],
    );

    const rejectKnowledge = useCallback(
        async (item: ExtractedKnowledgeItem) => {
            try {
                removeFromKnowledgeQueue(item.id);
                // TODO: Implement knowledge base rejection when backend is ready
            } catch (error) {
                console.error('Failed to reject memory:', error);
            }
        },
        [removeFromKnowledgeQueue],
    );

    const addToFeedbackQueue = useCallback(
        (
            feedbackContent: FeedbackContent,
            messageId: string,
            thread: string,
            traceId: string,
        ) => {
            const newItem: FeedbackItem = {
                id: uuidv4(),
                content: feedbackContent.value,
                messageId,
                threadId: thread,
                traceId,
            };
            dispatch({
                type: ActionType.ADD_TO_FEEDBACK_QUEUE,
                payload: [newItem],
            });
        },
        [],
    );

    const removeFromFeedbackQueue = useCallback((id: string) => {
        dispatch({
            type: ActionType.REMOVE_FROM_FEEDBACK_QUEUE,
            payload: id,
        });
    }, []);

    const handleJsonMessageContent = useCallback(
        (jsonContent: AgentJsonContent) => {
            if (getCurrentContext === jsonContent.value.context) {
                onSuccess(jsonContent);
                return;
            }
            const location = getContextLocation(jsonContent);
            if (location) {
                void navigate(location, {
                    state: {
                        ...getStateForNavigation(jsonContent),
                        source: getCurrentContext,
                    },
                });
            }
        },
        [
            getContextLocation,
            getCurrentContext,
            getStateForNavigation,
            navigate,
            onSuccess,
        ],
    );

    const fetchAndUpdateActiveThread = useCallback(
        async (threadId?: string) => {
            const currentThreadId =
                threadId ?? reducerState.activeThread?.threadId;
            if (!currentThreadId) return;
            const thread = await getAiChatThread(currentThreadId);
            dispatch({
                type: ActionType.UPDATE_ACTIVE_THREAD,
                payload: thread,
            });
        },
        [reducerState.activeThread?.threadId],
    );

    const handleThreadReceived = useCallback(
        (threadId: string, resourceId: string) => {
            // When threadId is received from stream, update active thread if needed
            const currentThread = reducerState.activeThread;
            if (!currentThread || currentThread.threadId !== threadId) {
                // Create or update thread with the received threadId
                const newThread: UserThreadMapping = {
                    threadId,
                    userUuid: resourceId || '',
                    createdAt: currentThread?.createdAt || new Date(),
                    threadName:
                        currentThread?.threadName || DEFAULT_THREAD_NAME,
                    updatedAt: new Date(),
                };

                const currentMessages = aiChatMessagesRef.current;

                dispatch({
                    type: ActionType.SET_ACTIVE_THREAD,
                    payload: {
                        thread: newThread,
                        messages: currentMessages,
                    },
                });
                // Persist thread to cookie
                handleThreadCreated(newThread, currentMessages);
            }
        },
        [reducerState.activeThread, handleThreadCreated],
    );

    const {
        sendMessage,
        isLoading: isStreamingMutation,
        abortStream,
    } = useAIStreamMutation({
        projectUuid: undefined, // Can be added later if needed
        context: reducerState.aiContext || getCurrentContext,
        onThreadReceived: handleThreadReceived,
        onStatusReceived: (status) => {
            setStatusText(status);
        },
        onMessagesReceived: (messages) => {
            messages.forEach((message) => {
                switch (message.content.type) {
                    case AiMessageTypes.MEMORY_EXTRACTED:
                        addToKnowledgeQueue(
                            message.content as MemoryContent,
                            message.messageId,
                            message.threadId,
                        );
                        break;
                    case AiMessageTypes.FEEDBACK:
                        addToFeedbackQueue(
                            message.content as FeedbackContent,
                            message.messageId,
                            message.threadId,
                            message.traceId || '',
                        );
                        break;
                    case AiMessageTypes.JSON:
                        handleJsonMessageContent(
                            message.content as AgentJsonContent,
                        );
                        break;
                    case AiMessageTypes.TEXT:
                    case AiMessageTypes.ERROR:
                    case AiMessageTypes.MEMORY_REFERENCED:
                    case AiMessageTypes.TABLE_JSON:
                        setStatusText(undefined);
                        setAIChatMessages([message]);
                        break;
                }
            });
        },
        onSuccess: async (threadId, _messages) => {
            const currentThread = reducerState.activeThread;
            if (
                !currentThread?.threadName ||
                (currentThread?.threadName === DEFAULT_THREAD_NAME &&
                    currentThread?.threadId === threadId)
            ) {
                void fetchAndUpdateActiveThread(threadId);
            }
        },
    });

    console.log('isStreamingMutation', isStreamingMutation);

    const pushTextToIAinput = useCallback((text: string) => {
        dispatch({
            type: ActionType.PUSH_TEXT_TO_INPUT,
            payload: text,
        });
    }, []);

    const clearPushedAiInputText = useCallback(() => {
        dispatch({
            type: ActionType.CLEAR_PUSHED_INPUT_TEXT,
        });
    }, []);

    useEffect(() => {
        if (reducerState.activeThread) return;
        if (hasInitializedRef.current) return;
        hasInitializedRef.current = true;

        void restoreThread();
    }, [restoreThread, reducerState.activeThread]);

    // Track the organizationId at mount so we can detect account switches
    const mountedOrgIdRef = useRef<string | undefined>(
        (() => {
            try {
                const userStr = localStorage.getItem('user');
                return userStr
                    ? JSON.parse(userStr).organizationId
                    : undefined;
            } catch {
                return undefined;
            }
        })(),
    );

    // Clear chat state when the user/org changes from another tab or on tab focus
    useEffect(() => {
        const getCurrentOrgId = (): string | undefined => {
            try {
                const userStr = localStorage.getItem('user');
                return userStr
                    ? JSON.parse(userStr).organizationId
                    : undefined;
            } catch {
                return undefined;
            }
        };

        const checkOrgChanged = () => {
            const currentOrgId = getCurrentOrgId();
            if (
                mountedOrgIdRef.current !== undefined &&
                currentOrgId !== mountedOrgIdRef.current
            ) {
                // Organization changed — clear chat state
                mountedOrgIdRef.current = currentOrgId;
                hasInitializedRef.current = false;
                dispatch({ type: ActionType.START_NEW_THREAD });
            }
        };

        // Fires when localStorage changes from another tab
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'user' || e.key === 'token') {
                checkOrgChanged();
            }
        };

        // Fires when this tab becomes visible again
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkOrgChanged();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, []);

    const startNewThread = useCallback(async () => {
        abortStream();
        pushTextToIAinput('');
        clearCurrentAiThreadCookie();
        dispatch({
            type: ActionType.START_NEW_THREAD,
        });
    }, [abortStream, clearCurrentAiThreadCookie, pushTextToIAinput]);

    const updateKnowledgeContent = useCallback(
        (id: string, content: string) => {
            dispatch({
                type: ActionType.UPDATE_KNOWLEDGE_CONTENT,
                payload: { id, content },
            });
        },
        [],
    );

    const openFeedbackModal = useCallback(
        (messageId: string, threadId: string, traceId: string) => {
            currentFeedbackModalDataRef.current = {
                messageId,
                threadId,
                traceId,
            };
            dispatch({
                type: ActionType.OPEN_FEEDBACK_MODAL,
                payload: { messageId, threadId, traceId },
            });
        },
        [],
    );

    const closeFeedbackModal = useCallback(() => {
        currentFeedbackModalDataRef.current = null;
        dispatch({
            type: ActionType.CLOSE_FEEDBACK_MODAL,
        });
    }, []);

    const getCurrentFeedbackModalData = useCallback(() => {
        return currentFeedbackModalDataRef.current;
    }, []);

    const handleSendMessage = useCallback(
        async (payload: StreamMessage) => {
            if (payload.content.trim() === '') return;
            const activeThread = reducerState.activeThread;
            const threadId = activeThread?.threadId;

            // Add user message to chat (append so UI shows immediately)
            const optimisticMessage: AgentMessage = {
                threadId: threadId || '',
                messageId: payload.messageId || uuidv4(),
                author: Author.USER,
                context: reducerState.aiContext ?? getCurrentContext,
                content: {
                    type: AiMessageTypes.TEXT,
                    value: payload.content,
                },
                traceId: payload.traceId || '',
            };
            setAIChatMessages([
                ...reducerState.aiChatMessages,
                optimisticMessage,
            ]);

            // Scroll to bottom so the new question and incoming answer stay in view
            window.requestAnimationFrame(() => {
                window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: 'smooth',
                });
            });

            let messageToSend = payload;

            // Info: Inject hidden context for the message
            try {
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const user = JSON.parse(userStr);
                    if (user.organizationId) {
                        messageToSend = {
                            ...payload,
                            content: `${payload.content} \n\n Context: Merchant ID ${user.organizationId}`,
                        };
                    }
                }
            } catch (e) {
                console.error('Failed to inject organization context', e);
            }

            // Send message with threadId (will be created by backend if not provided)
            await sendMessage(messageToSend, threadId);
            setAiContext(null);
        },
        [
            reducerState.activeThread,
            reducerState.aiChatMessages,
            reducerState.aiContext,
            setAIChatMessages,
            getCurrentContext,
            sendMessage,
            setAiContext,
        ],
    );

    const setActiveThread = useCallback(async (id: string) => {
        setIsFetchingThreadMessages(true);
        try {
            const results = await Promise.allSettled([
                getAiChatMessages(id),
                getAiChatThread(id),
            ] as const);

            const [messagesResult, threadResult] = results;

            if (
                threadResult.status === 'fulfilled' &&
                messagesResult.status === 'fulfilled'
            ) {
                // setAiThreadCookie can be called here if projectUuid is available
                dispatch({
                    type: ActionType.SET_ACTIVE_THREAD,
                    payload: {
                        thread: threadResult.value,
                        messages: messagesResult.value,
                    },
                });
            }
        } catch (error) {
            console.error('Failed to fetch thread messages:', error);
        } finally {
            setIsFetchingThreadMessages(false);
        }
    }, []);

    const deleteThread = useCallback(
        async (threadId: string) => {
            await mutateAsyncDeleteThread(threadId);
            if (threadId === reducerState.activeThread?.threadId) {
                await handleCreateThread();
            }
        },
        [
            mutateAsyncDeleteThread,
            handleCreateThread,
            reducerState.activeThread?.threadId,
        ],
    );

    const updateThreadById = useCallback(
        async (threadId: string, payload: UpdateThreadPayload) => {
            await mutateAsyncUpdateThread({ threadId, payload });
            void fetchAndUpdateActiveThread(threadId);
        },
        [mutateAsyncUpdateThread, fetchAndUpdateActiveThread],
    );

    const state = useMemo(
        () => ({
            ...reducerState,
            isStreaming: isStreamingMutation,
            isLoadingActiveThreadMessages,
            isDeletingThread,
            statusText: isStreamingMutation ? statusText : undefined,
        }),
        [
            reducerState,
            isStreamingMutation,
            isLoadingActiveThreadMessages,
            isDeletingThread,
            statusText,
        ],
    );

    const actions = useMemo(
        () => ({
            setShowAISidebar,
            registerCallback,
            unregisterCallback,
            onSuccess,
            setAiContext,
            setAIChatMessages,
            handleCreateThread,
            addStreamedMessageId,
            setUserFeedback,
            addToKnowledgeQueue,
            removeFromKnowledgeQueue,
            acceptKnowledge,
            rejectKnowledge,
            addToFeedbackQueue,
            removeFromFeedbackQueue,
            handleSendMessage,
            pushTextToIAinput,
            clearPushedAiInputText,
            abortStream,
            startNewThread,
            updateKnowledgeContent,
            openFeedbackModal,
            closeFeedbackModal,
            getCurrentFeedbackModalData,
            setActiveThread,
            deleteThread,
            updateThreadById,
        }),
        [
            setShowAISidebar,
            registerCallback,
            unregisterCallback,
            onSuccess,
            setAiContext,
            setAIChatMessages,
            handleCreateThread,
            addStreamedMessageId,
            setUserFeedback,
            addToKnowledgeQueue,
            removeFromKnowledgeQueue,
            acceptKnowledge,
            rejectKnowledge,
            addToFeedbackQueue,
            removeFromFeedbackQueue,
            handleSendMessage,
            pushTextToIAinput,
            clearPushedAiInputText,
            abortStream,
            startNewThread,
            updateKnowledgeContent,
            openFeedbackModal,
            closeFeedbackModal,
            getCurrentFeedbackModalData,
            setActiveThread,
            deleteThread,
            updateThreadById,
        ],
    );

    const value: AiAnalystContext = useMemo(
        () => ({
            state,
            actions,
        }),
        [state, actions],
    );

    return (
        <AiAnalystProviderContext.Provider value={value}>
            {children}
        </AiAnalystProviderContext.Provider>
    );
};

export default AiAnalystProvider;
